/**
 * POS 80mm Printer Service
 * Handles printing receipts to POS thermal printers
 */

class PosPrinterService {
  constructor() {
    this.printedReceipts = this.loadPrintedReceipts();
  }

  /**
   * Load printed receipts from localStorage
   */
  loadPrintedReceipts() {
    try {
      const stored = localStorage.getItem('printedReceipts');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading printed receipts:', error);
      return [];
    }
  }

  /**
   * Save printed receipts to localStorage
   */
  savePrintedReceipts() {
    try {
      localStorage.setItem('printedReceipts', JSON.stringify(this.printedReceipts));
    } catch (error) {
      console.error('Error saving printed receipts:', error);
    }
  }

  /**
   * Mark a receipt as printed
   */
  markReceiptAsPrinted(receiptId, receiptData) {
    const printedReceipt = {
      id: receiptId,
      printedAt: new Date().toISOString(),
      data: receiptData
    };

    // Check if already printed
    const existingIndex = this.printedReceipts.findIndex(r => r.id === receiptId);
    if (existingIndex >= 0) {
      this.printedReceipts[existingIndex] = printedReceipt;
    } else {
      this.printedReceipts.push(printedReceipt);
    }

    this.savePrintedReceipts();
  }

  /**
   * Check if a receipt has been printed
   */
  isReceiptPrinted(receiptId) {
    return this.printedReceipts.some(r => r.id === receiptId);
  }

  /**
   * Get printed receipt info
   */
  getPrintedReceiptInfo(receiptId) {
    return this.printedReceipts.find(r => r.id === receiptId);
  }

  /**
   * Clear all printed receipts (for testing/admin purposes)
   */
  clearPrintedReceipts() {
    this.printedReceipts = [];
    this.savePrintedReceipts();
  }

  /**
   * Format receipt for POS 80mm printer
   */
  formatReceiptForPos(receiptData, facilityInfo = {}) {
    const { 
      patientName, 
      receiptNo, 
      doctorName, 
      prescriptionDate, 
      items = [], 
      totalAmount,
      irsAmount,
      beneficiaryCash,
      hospitalName
    } = receiptData;

    const facilityName = facilityInfo.name || 'Healthcare Facility';
    const facilityAddress = facilityInfo.address || '';
    const facilityPhone = facilityInfo.phone || '';

    // POS 80mm receipt format (typically 32 characters wide)
    const lineWidth = 32;
    const centerText = (text, width = lineWidth) => {
      const padding = Math.max(0, width - text.length);
      const leftPad = Math.floor(padding / 2);
      return ' '.repeat(leftPad) + text;
    };

    const separator = '='.repeat(lineWidth);
    const dashSeparator = '-'.repeat(lineWidth);

    let receipt = '';

    // Header
    receipt += centerText(facilityName) + '\n';
    if (facilityAddress) {
      receipt += centerText(facilityAddress) + '\n';
    }
    if (facilityPhone) {
      receipt += centerText(facilityPhone) + '\n';
    }
    receipt += separator + '\n';
    receipt += centerText('ENTITLEMENT RECEIPT') + '\n';
    receipt += separator + '\n';

    // Receipt details
    receipt += `Receipt No: ${receiptNo || 'N/A'}\n`;
    receipt += `Date: ${new Date().toLocaleDateString()}\n`;
    receipt += `Time: ${new Date().toLocaleTimeString()}\n`;
    receipt += dashSeparator + '\n';

    // Patient info
    receipt += `Patient: ${patientName || 'N/A'}\n`;
    receipt += `Doctor: ${doctorName || 'N/A'}\n`;
    receipt += `Hospital: ${hospitalName || 'N/A'}\n`;
    receipt += `Prescription Date: ${prescriptionDate || 'N/A'}\n`;
    receipt += dashSeparator + '\n';

    // Items
    receipt += centerText('ITEMS DISPENSED') + '\n';
    receipt += dashSeparator + '\n';
    
    if (items && items.length > 0) {
      items.forEach((item, index) => {
        const itemName = item.name || 'Unknown Item';
        const quantity = item.quantity || 0;
        const price = parseFloat(item.price || 0).toFixed(2);
        const amount = parseFloat(item.amount || 0).toFixed(2);
        
        // Truncate long item names
        const displayName = itemName.length > 20 ? itemName.substring(0, 17) + '...' : itemName;
        
        receipt += `${index + 1}. ${displayName}\n`;
        receipt += `   Qty: ${quantity} x $${price} = $${amount}\n`;
      });
    } else {
      receipt += 'No items listed\n';
    }

    receipt += dashSeparator + '\n';

    // Totals
    receipt += `IRS Amount:     $${parseFloat(irsAmount || 0).toFixed(2)}\n`;
    receipt += `Beneficiary:    $${parseFloat(beneficiaryCash || 0).toFixed(2)}\n`;
    receipt += separator + '\n';
    receipt += `TOTAL AMOUNT:   $${parseFloat(totalAmount || 0).toFixed(2)}\n`;
    receipt += separator + '\n';

    // Footer
    receipt += centerText('Thank you for your visit!') + '\n';
    receipt += centerText('Keep this receipt safe') + '\n';
    receipt += '\n\n\n'; // Extra space for cutting

    return receipt;
  }

  /**
   * Print receipt to POS printer
   */
  async printReceipt(receiptData, facilityInfo = {}) {
    try {
      const formattedReceipt = this.formatReceiptForPos(receiptData, facilityInfo);
      
      // Method 1: Try Web Serial API (for direct USB connection)
      if ('serial' in navigator) {
        return await this.printViaSerial(formattedReceipt);
      }
      
      // Method 2: Try WebUSB API (for USB printers)
      if ('usb' in navigator) {
        return await this.printViaUSB(formattedReceipt);
      }
      
      // Method 3: Fallback to window.print with POS styling
      return await this.printViaWindow(formattedReceipt, receiptData);
      
    } catch (error) {
      console.error('Error printing receipt:', error);
      throw new Error('Failed to print receipt: ' + error.message);
    }
  }

  /**
   * Print via Web Serial API
   */
  async printViaSerial(receiptText) {
    try {
      const port = await navigator.serial.requestPort();
      await port.open({ baudRate: 9600 });
      
      const writer = port.writable.getWriter();
      const encoder = new TextEncoder();
      const data = encoder.encode(receiptText);
      
      await writer.write(data);
      writer.releaseLock();
      await port.close();
      
      return { success: true, method: 'serial' };
    } catch (error) {
      throw new Error('Serial printing failed: ' + error.message);
    }
  }

  /**
   * Print via WebUSB API
   */
  async printViaUSB(receiptText) {
    try {
      const device = await navigator.usb.requestDevice({
        filters: [{ classCode: 7 }] // Printer class
      });
      
      await device.open();
      await device.selectConfiguration(1);
      await device.claimInterface(0);
      
      const encoder = new TextEncoder();
      const data = encoder.encode(receiptText);
      
      await device.transferOut(1, data);
      await device.close();
      
      return { success: true, method: 'usb' };
    } catch (error) {
      throw new Error('USB printing failed: ' + error.message);
    }
  }

  /**
   * Print via window.print with POS styling
   */
  async printViaWindow(receiptText, receiptData) {
    return new Promise((resolve, reject) => {
      try {
        const printWindow = window.open('', '_blank', 'width=400,height=600');
        
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>Receipt - ${receiptData.receiptNo || 'N/A'}</title>
              <style>
                @media print {
                  @page { 
                    size: 80mm auto; 
                    margin: 0; 
                  }
                  body { 
                    font-family: 'Courier New', monospace; 
                    font-size: 12px; 
                    line-height: 1.2; 
                    margin: 0; 
                    padding: 5px; 
                    width: 80mm; 
                    white-space: pre-line; 
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                  }
                  .no-print { display: none; }
                  img {
                    max-width: 100%;
                    height: auto;
                  }
                }
                @media screen {
                  body { 
                    font-family: 'Courier New', monospace; 
                    font-size: 12px; 
                    line-height: 1.2; 
                    margin: 0; 
                    padding: 5px; 
                    width: 80mm; 
                    white-space: pre-line; 
                    background: white;
                  }
                }
                .print-button {
                  position: fixed;
                  top: 10px;
                  right: 10px;
                  background: #007bff;
                  color: white;
                  border: none;
                  padding: 10px 20px;
                  border-radius: 5px;
                  cursor: pointer;
                }
                .logo-container {
                  text-align: center;
                  margin-bottom: 10px;
                }
                .logo-placeholder {
                  width: 100%;
                  height: 40px;
                  border: 1px solid #333;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  font-size: 10px;
                  color: #666;
                  background-color: #f8f9fa;
                }
              </style>
            </head>
            <body>
              <button class="print-button no-print" onclick="window.print()">Print Receipt</button>
              <div class="logo-container">
                <div class="logo-placeholder">LOGO</div>
              </div>
              <pre>${receiptText}</pre>
              <script>
                window.onload = function() {
                  setTimeout(() => {
                    window.print();
                    setTimeout(() => {
                      window.close();
                    }, 1000);
                  }, 500);
                };
              </script>
            </body>
          </html>
        `);
        
        printWindow.document.close();
        
        printWindow.onbeforeunload = () => {
          resolve({ success: true, method: 'window' });
        };
        
        printWindow.onerror = (error) => {
          reject(new Error('Window printing failed: ' + error.message));
        };
        
      } catch (error) {
        reject(new Error('Failed to open print window: ' + error.message));
      }
    });
  }
}

// Create singleton instance
const posPrinterService = new PosPrinterService();

export default posPrinterService;
